import React, { useState, useCallback, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { technologies, resources, buildings } from '../data';

// Panels
import { ResourcePanel, BuildingPanel, QuestPanel, TechPanel, MonumentsPanel, CraftingPanel } from './panels';
import WorldMapPanel from './panels/WorldMapPanel';
import PlayerPanel from './panels/PlayerPanel';
import DungeonPanel from './panels/DungeonPanel';
import ForgePanel from './panels/ForgePanel';
import LifeSkillsPanel from './panels/LifeSkillsPanel';
import DevToolsPanel from './panels/DevToolsPanel';
import DiplomacyPanel from './panels/DiplomacyPanel';

// Modals
import { StatsModal, AdvisorsModal, SpecializationModal, TechTreeModal, GuideModal } from './modals';

// Tutorial & Guided Tours
import TutorialManager from './TutorialManager';
import GuidedTourManager from './GuidedTourManager';

// Kingdom Visual
import KingdomVisual from './KingdomVisual';

// Victory Screen
import VictoryScreen from './VictoryScreen';

// Common Components
import { AgeProgressBar } from './common';

// UI Components
import SaveManager from './UI/SaveManager';

function MainView() {
  const {
    playerResources,
    buildingCounts,
    researchedTechs,
    currentResearch,
    researchProgress,
    researchStartTime,
    activeQuest,
    completedQuests,
    purchasedUpgrades,
    purchasedClickUpgrades,
    purchasedResearchUpgrades,
    unlockedAdvisors,
    activeAdvisors,
    advisorSlots,
    buildingSpecializations,
    stats,
    kingdomTier,
    totalBuildings,
    hasWon,
    resetGame,
    playerCharacter,
    // Wonder state
    builtWonders,
    currentWonderConstruction,
    // World Map state
    worldMap,
    // Tutorial state
    currentTutorial,
    dismissTutorial,
    triggerTutorial,
    hasSaveData,
    // Guided Tour state
    currentGuidedTour,
    guidedTourActions,
    completeGuidedTour,
    skipGuidedTour,
  } = useGame();

  // Victory screen state (can dismiss to continue playing)
  const [showVictory, setShowVictory] = useState(true);

  // UI State
  const [notifications, setNotifications] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  
  // Center panel tab state
  const [centerTab, setCenterTab] = useState('worldmap');

  // Modal State
  const [showTechTree, setShowTechTree] = useState(false);
  const [showAdvisors, setShowAdvisors] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guideCategory, setGuideCategory] = useState('getting-started');
  const [guideTopic, setGuideTopic] = useState('basics');
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  // Highlight state for quest navigation
  const [highlightedBuilding, setHighlightedBuilding] = useState(null);
  const [highlightedTech, setHighlightedTech] = useState(null);

  // Great Works placement mode
  const [greatWorkPlacement, setGreatWorkPlacement] = useState(null);

  // Dev Tools panel (toggle with F9)
  const [showDevTools, setShowDevTools] = useState(false);

  // Add notification - BOTTOM CENTER, MAX 3
  // Longer duration for important events
  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random(); // More unique ID
    
    setNotifications(prev => {
      // Avoid duplicates
      if (prev.some(n => n.message === message)) return prev;
      return [...prev, { id, message, type }].slice(-3);
    });
    
    // Duration based on type
    const duration = (type === 'success' || type === 'error' || type === 'warning') ? 5000 : 3000;
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  }, []);

  // Handle Great Work selection for placement
  const handleSelectGreatWork = useCallback((workId, validPlacements) => {
    setGreatWorkPlacement({ workId, validPlacements });
    setCenterTab('worldmap');
  }, []);

  // Cancel Great Work placement mode
  const cancelGreatWorkPlacement = useCallback(() => {
    setGreatWorkPlacement(null);
  }, []);

  // F9 key to toggle dev tools
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F9') {
        e.preventDefault();
        setShowDevTools(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Trigger gameStart tutorial for new players
  useEffect(() => {
    if (!hasSaveData()) {
      const timer = setTimeout(() => {
        triggerTutorial('gameStart');
      }, 1000); // Show after 1 second
      return () => clearTimeout(timer);
    }
  }, []); // Run once on mount

  // Trigger Buildings tab tutorial on first click
  useEffect(() => {
    if (centerTab === 'buildings') {
      triggerTutorial('buildingsTab');
    }
  }, [centerTab, triggerTutorial]);

  // Trigger Monuments tab tutorial on first click
  useEffect(() => {
    if (centerTab === 'wonders') {
      triggerTutorial('monumentsTab');
    }
  }, [centerTab, triggerTutorial]);

  // Trigger Forge tab tutorial on first click
  useEffect(() => {
    if (centerTab === 'forge') {
      triggerTutorial('forgeTab');
    }
  }, [centerTab, triggerTutorial]);

  // Trigger Character tab tutorial on first click
  useEffect(() => {
    if (centerTab === 'character') {
      triggerTutorial('characterTab');
    }
  }, [centerTab, triggerTutorial]);

  // Trigger Advisors button tutorial on first click
  useEffect(() => {
    if (showAdvisors) {
      triggerTutorial('advisorsButton');
    }
  }, [showAdvisors, triggerTutorial]);

  // Tooltip handlers
  const showTooltip = useCallback((text, e) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({ text, x: rect.left, y: rect.bottom + 5 });
  }, []);
  
  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Show victory screen when won (AFTER all hooks)
  if (hasWon && showVictory) {
    return (
      <VictoryScreen 
        onContinue={() => setShowVictory(false)}
        onRestart={() => {
          resetGame();
          window.location.reload();
        }}
      />
    );
  }

  // Count discovered nodes for the tab badge
  const discoveredNodes = worldMap?.discoveredNodes ? Object.keys(worldMap.discoveredNodes).length : 1;
  const outpostCount = worldMap?.outposts ? Object.keys(worldMap.outposts).length : 0;

  return (
    <div className="main-view">
      {/* Tooltip */}
      {tooltip && (
        <div className="tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.text}
        </div>
      )}

      {/* Notifications - BOTTOM CENTER */}
      <div className="notifications-br">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            {notif.message}
          </div>
        ))}
      </div>

      {/* Top Navigation */}
      <nav className="top-nav">
        <div className="nav-brand">
          <span className="kingdom-icon">⚔️</span>
          <span className="brand-title">Civ-IDLE</span>
          <span className="brand-tier">Tier {kingdomTier}</span>
        </div>

        <div className="nav-actions">
          <button className="nav-btn guide" onClick={() => {
            setGuideCategory('getting-started');
            setGuideTopic('basics');
            setShowGuide(true);
          }}>📖 Guide</button>
          <button className="nav-btn advisors" onClick={() => setShowAdvisors(true)}>👑 Advisors</button>
          <button className="nav-btn stats" onClick={() => setShowStats(true)}>📊 Stats</button>
          <SaveManager onNotification={addNotification} />
        </div>
      </nav>

      {/* Age Progress Bar */}
      <AgeProgressBar />

      {/* Tutorial Manager */}
      <TutorialManager
        currentTutorial={currentTutorial}
        onDismiss={dismissTutorial}
        gameState={{ buildingCounts, researchedTechs, completedQuests, stats }}
      />

      {/* Guided Tour Manager - uses context for modal control */}
      <GuidedTourManager
        currentTour={currentGuidedTour}
        onComplete={completeGuidedTour}
        onSkip={skipGuidedTour}
        tourActions={guidedTourActions}
      />

      {/* Modals */}
      {showStats && <StatsModal onClose={() => setShowStats(false)} />}
      {showAdvisors && <AdvisorsModal onClose={() => setShowAdvisors(false)} onNotification={addNotification} />}
      {showGuide && <GuideModal onClose={() => setShowGuide(false)} initialCategory={guideCategory} initialTopic={guideTopic} />}
      {showTechTree && <TechTreeModal onClose={() => setShowTechTree(false)} onNotification={addNotification} highlightedTech={highlightedTech} />}
      {selectedBuilding && (
        <SpecializationModal
          buildingId={selectedBuilding}
          onClose={() => setSelectedBuilding(null)}
          onNotification={addNotification}
        />
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Left: Kingdom + Resources */}
        <div className="left-panel">
          <KingdomVisual 
            tier={kingdomTier} 
            population={Math.floor(playerResources.population || 0)}
            totalBuildings={totalBuildings}
          />
          <ResourcePanel 
            onNotification={addNotification}
            showTooltip={showTooltip}
            hideTooltip={hideTooltip}
          />
        </div>

        {/* Center: World Map / Buildings / Wonders Tabs */}
        <div className="center-panel-wrapper">
          {/* Tab Buttons with Dropdown Groups */}
          <div className="center-tabs">
            {/* Character Group - Character, Life Skills */}
            <div className={`center-tab-group ${['character', 'lifeskills'].includes(centerTab) ? 'active' : ''}`}>
              <button
                className="center-tab-main"
                onClick={() => setCenterTab('character')}
              >
                🗡️ Character
                <span className="tab-arrow">▼</span>
              </button>
              <div className="center-tab-dropdown">
                <button
                  className={`dropdown-item ${centerTab === 'character' ? 'active' : ''}`}
                  onClick={() => setCenterTab('character')}
                >
                  🗡️ Stats & Gear
                </button>
                <button
                  className={`dropdown-item ${centerTab === 'lifeskills' ? 'active' : ''}`}
                  onClick={() => setCenterTab('lifeskills')}
                >
                  🌿 Life Skills
                </button>
              </div>
            </div>

            {/* Explore Group - World Map, Diplomacy */}
            <div className={`center-tab-group ${['worldmap', 'diplomacy'].includes(centerTab) ? 'active' : ''}`}>
              <button
                className="center-tab-main"
                onClick={() => setCenterTab('worldmap')}
              >
                🗺️ Explore
                {outpostCount > 0 && <span className="tab-badge">{outpostCount}</span>}
                <span className="tab-arrow">▼</span>
              </button>
              <div className="center-tab-dropdown">
                <button
                  className={`dropdown-item ${centerTab === 'worldmap' ? 'active' : ''}`}
                  onClick={() => setCenterTab('worldmap')}
                >
                  🗺️ World Map
                  {outpostCount > 0 && <span className="tab-badge">{outpostCount}</span>}
                </button>
                {Object.keys(worldMap.discoveredKingdoms || {}).length > 0 && (
                  <button
                    className={`dropdown-item ${centerTab === 'diplomacy' ? 'active' : ''}`}
                    onClick={() => setCenterTab('diplomacy')}
                  >
                    👑 Diplomacy
                    <span className="tab-badge">{Object.keys(worldMap.discoveredKingdoms || {}).length}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Kingdom Group - Buildings, Monuments */}
            <div className={`center-tab-group ${['buildings', 'wonders'].includes(centerTab) ? 'active' : ''}`}>
              <button
                className="center-tab-main"
                onClick={() => setCenterTab('buildings')}
              >
                🏰 Kingdom
                {currentWonderConstruction && <span className="tab-indicator">⏳</span>}
                <span className="tab-arrow">▼</span>
              </button>
              <div className="center-tab-dropdown">
                <button
                  className={`dropdown-item ${centerTab === 'buildings' ? 'active' : ''}`}
                  onClick={() => setCenterTab('buildings')}
                >
                  🏗️ Buildings
                </button>
                <button
                  className={`dropdown-item ${centerTab === 'wonders' ? 'active' : ''}`}
                  onClick={() => setCenterTab('wonders')}
                >
                  🏛️ Monuments
                  {currentWonderConstruction && <span className="tab-indicator">⏳</span>}
                  {builtWonders?.length > 0 && !currentWonderConstruction && (
                    <span className="tab-badge">{builtWonders.length}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Equipment Group - Forge, Crafting */}
            <div className={`center-tab-group ${['forge', 'crafting'].includes(centerTab) ? 'active' : ''}`}>
              <button
                className="center-tab-main"
                onClick={() => setCenterTab('forge')}
              >
                ⚒️ Equipment
                <span className="tab-arrow">▼</span>
              </button>
              <div className="center-tab-dropdown">
                <button
                  className={`dropdown-item ${centerTab === 'forge' ? 'active' : ''}`}
                  onClick={() => setCenterTab('forge')}
                >
                  ⚒️ Forge
                </button>
                <button
                  className={`dropdown-item ${centerTab === 'crafting' ? 'active' : ''}`}
                  onClick={() => setCenterTab('crafting')}
                >
                  🔨 Crafting
                </button>
              </div>
            </div>
          </div>
          
          {/* Tab Content */}
          {centerTab === 'character' && (
            <PlayerPanel />
          )}
          {centerTab === 'lifeskills' && (
            <LifeSkillsPanel />
          )}
          {centerTab === 'buildings' && (
            <BuildingPanel
              onNotification={addNotification}
              onSpecialize={setSelectedBuilding}
              highlightedBuilding={highlightedBuilding}
            />
          )}
          {centerTab === 'wonders' && (
            <MonumentsPanel
              onNotification={addNotification}
              onSelectGreatWork={handleSelectGreatWork}
            />
          )}
          {/* WorldMap/Dungeon switching - conditional render */}
          {/* Tile cache is module-level singleton, persists across remounts */}
          {centerTab === 'worldmap' && (
            playerCharacter.isInDungeon ? (
              <DungeonPanel />
            ) : (
              <WorldMapPanel
                greatWorkPlacement={greatWorkPlacement}
                onCancelGreatWorkPlacement={cancelGreatWorkPlacement}
                onNotification={addNotification}
              />
            )
          )}
          {centerTab === 'forge' && (
            <ForgePanel />
          )}
          {centerTab === 'diplomacy' && (
            <DiplomacyPanel />
          )}
          {centerTab === 'crafting' && (
            <CraftingPanel />
          )}
        </div>

        {/* Right: Quest + Tech */}
        <div className="right-panel">
          <QuestPanel
            onNotification={addNotification}
            onNavigateToBuilding={(buildingId) => {
              setCenterTab('buildings');
              setHighlightedBuilding(buildingId);
              setTimeout(() => setHighlightedBuilding(null), 3000);
            }}
            onNavigateToTech={(techId) => {
              setShowTechTree(true);
              setHighlightedTech(techId);
              setTimeout(() => setHighlightedTech(null), 3000);
            }}
            onNavigateToWorldMap={() => {
              setCenterTab('worldmap');
            }}
          />
          <TechPanel
            onNotification={addNotification}
            onOpenTechTree={() => setShowTechTree(true)}
            highlightedTech={highlightedTech}
          />
        </div>
      </div>

      {/* Dev Tools (F9 to toggle) */}
      <DevToolsPanel
        isOpen={showDevTools}
        onClose={() => setShowDevTools(false)}
      />
    </div>
  );
}

export default MainView;
